import { useEffect, useRef, useState, type ComponentType, type FocusEvent, type KeyboardEvent, type PointerEvent } from "react";
import { CalendarClock, CalendarDays, ChevronLeft, ChevronRight, Package, Target, Users, Wallet, type LucideIcon } from "lucide-react";

import { cn } from "../lib/utils.js";
import {
  AssetsVisual,
  AttendanceVisual,
  EmployeesVisual,
  LeaveVisual,
  PayrollVisual,
  PerformanceVisual,
} from "./HeroSlideVisuals.js";

/** How long each slide stays up while the carousel is playing. */
const SLIDE_MS = 5500;
/** A horizontal drag longer than this, in px, turns the slide. */
const SWIPE_PX = 40;

interface Slide {
  name: string;
  icon: LucideIcon;
  headline: string;
  description: string;
  tags: string[];
  Visual: ComponentType;
}

const SLIDES: Slide[] = [
  {
    name: "Employees",
    icon: Users,
    headline: "Every employee, one accurate record.",
    description: "Keep profiles, documents and onboarding together, with an org chart that shows who reports to whom.",
    tags: ["Profiles", "Onboarding", "Org chart"],
    Visual: EmployeesVisual,
  },
  {
    name: "Attendance",
    icon: CalendarClock,
    headline: "Time and attendance you can trust.",
    description: "Track shifts and check-ins as they happen, and send corrections to the right manager for approval.",
    tags: ["Shifts", "Check-ins", "Approvals"],
    Visual: AttendanceVisual,
  },
  {
    name: "Leave",
    icon: CalendarDays,
    headline: "Time off, handled end to end.",
    description: "Employees request in seconds, managers approve from one queue, and balances update automatically.",
    tags: ["Requests", "Approvals", "Balances"],
    Visual: LeaveVisual,
  },
  {
    name: "Payroll",
    icon: Wallet,
    headline: "Payroll built on the same records.",
    description: "Upload a payroll file and check each row against its employee record, with mismatches flagged for review.",
    tags: ["Upload", "Discrepancies", "Line items"],
    Visual: PayrollVisual,
  },
  {
    name: "Performance",
    icon: Target,
    headline: "Reviews that are clear and consistent.",
    description: "Set goals, run review cycles and keep feedback alongside each employee's record.",
    tags: ["Org goals", "Skills", "Reviews"],
    Visual: PerformanceVisual,
  },
  {
    name: "Assets",
    icon: Package,
    headline: "Know where every device is.",
    description: "Track company laptops, phones and equipment from assignment to return, so nothing goes missing.",
    tags: ["Assignment", "Tracking", "Returns"],
    Visual: AssetsVisual,
  },
];

const pad = (n: number) => String(n).padStart(2, "0");

/** Reduced motion turns autoplay off; the slides still step by hand. `?motion=on` lets QA test with the OS setting on. */
const prefersReduced = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches &&
  !new URLSearchParams(window.location.search).has("motion");

/**
 * Auto-playing module carousel for the sign-in page. Pauses on hover and keyboard focus,
 * steps by dots, arrows, swipe or arrow keys, and wraps around. Styled by the `.hx-*` rules in tokens.css.
 */
export function HeroCarousel() {
  const [reduce] = useState(prefersReduced);
  const [idx, setIdx] = useState(0);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  /** Bumped on every slide change and on resume, so the progress bar restarts from zero. */
  const [tick, setTick] = useState(0);
  const swipeStart = useRef<number | null>(null);

  const count = SLIDES.length;
  const paused = hovered || focused || reduce;
  const carouselState = reduce ? "stopped" : paused ? "paused" : "playing";

  const go = (to: number) => {
    setIdx(((to % count) + count) % count);
    setTick((t) => t + 1);
  };

  // Advances every SLIDE_MS while playing. Cleared on pause, on any change and on unmount.
  useEffect(() => {
    if (paused) return;
    const id = window.setTimeout(() => {
      setIdx((i) => (i + 1) % count);
      setTick((t) => t + 1);
    }, SLIDE_MS);
    return () => window.clearTimeout(id);
  }, [idx, tick, paused, count]);

  // Resuming restarts the progress bar, so the next slide always gets the full SLIDE_MS.
  useEffect(() => {
    if (!paused) setTick((t) => t + 1);
  }, [paused]);

  const onFocus = (e: FocusEvent<HTMLDivElement>) => {
    // Keyboard focus pauses. A mouse click does not, so the carousel resumes once the pointer leaves.
    if (e.target instanceof HTMLElement && e.target.matches(":focus-visible")) setFocused(true);
  };
  const onBlur = (e: FocusEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFocused(false);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "ArrowRight") go(idx + 1);
    else if (e.key === "ArrowLeft") go(idx - 1);
    else return;
    e.preventDefault();
  };

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    swipeStart.current = e.clientX;
  };
  const onPointerUp = (e: PointerEvent<HTMLDivElement>) => {
    const start = swipeStart.current;
    swipeStart.current = null;
    if (start === null) return;
    const dx = e.clientX - start;
    if (Math.abs(dx) > SWIPE_PX) go(idx + (dx < 0 ? 1 : -1));
  };

  return (
    <div
      className="hx-root"
      data-reduce={reduce ? "true" : "false"}
      data-carousel-state={carouselState}
      data-carousel-index={idx}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={onFocus}
      onBlur={onBlur}
    >
      <div className="hx-inner">
        <div
          className="hx-viewport login-intro-row [--i:5]"
          tabIndex={0}
          role="region"
          aria-roledescription="carousel"
          aria-label="HRMS modules"
          aria-live={paused ? "polite" : "off"}
          onKeyDown={onKeyDown}
          onPointerDown={onPointerDown}
          onPointerUp={onPointerUp}
          onPointerCancel={() => {
            swipeStart.current = null;
          }}
        >
          <div className="hx-track" data-index={idx}>
            {SLIDES.map(({ name, icon: Icon, headline, description, tags, Visual }, i) => {
              const active = i === idx;
              return (
                <div
                  key={name}
                  className={cn("hx-slide", active && "hx-on")}
                  role="group"
                  aria-roledescription="slide"
                  aria-label={`${i + 1} of ${count}: ${name}`}
                  aria-hidden={!active}
                >
                  <div className="hx-card2">
                    <div className="hx-cBody">
                      <div className="hx-cTop">
                        <span className="hx-tile">
                          <Icon size={17} aria-hidden="true" />
                        </span>
                        <span className="hx-eyebrow">{name}</span>
                        <span className="hx-count">
                          {pad(i + 1)} / {pad(count)}
                        </span>
                      </div>
                      <div className="hx-cHead">{headline}</div>
                      <div className="hx-cDesc">{description}</div>
                      <div className="hx-cChips">
                        {tags.map((tag) => (
                          <span key={tag} className="hx-cp">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="hx-vis" aria-hidden="true">
                      <Visual />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="hx-ctrl login-intro-row [--i:6]">
          <div className="hx-dots">
            {SLIDES.map(({ name }, i) => {
              const active = i === idx;
              const fill = !active ? undefined : reduce ? "hx-full" : tick % 2 === 0 ? "hx-fill-0" : "hx-fill-1";
              return (
                <button
                  key={name}
                  type="button"
                  className="hx-dtab"
                  aria-label={`${name} (${i + 1} of ${count})`}
                  aria-current={active ? "true" : undefined}
                  onClick={() => go(i)}
                >
                  <i className={fill} />
                </button>
              );
            })}
          </div>
          <div className="hx-btns">
            <button type="button" className="hx-rb" aria-label="Previous module" onClick={() => go(idx - 1)}>
              <ChevronLeft size={18} aria-hidden="true" />
            </button>
            <button type="button" className="hx-rb" aria-label="Next module" onClick={() => go(idx + 1)}>
              <ChevronRight size={18} aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
